import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, ExpressCheckoutElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Loader2, Lock, ShieldCheck, ShoppingBag, Search, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { API, publicApi } from '@/lib/api';
import { injectLinkPixels, fireLinkPixel } from '@/components/PixelTracker';
import { resolveImageUrl } from '@/components/ImageUpload';
import { COUNTRIES } from '@/constants/countries';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { payLinkT, resolvePayLinkLang } from '@/lib/paylinkI18n';

const SHOP_FONT = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const StepTitle = ({ n, children }) => (
  <h2 className="flex items-center gap-2.5 text-[22px] leading-tight font-bold text-[#1a1a1a] tracking-tight">
    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#1a1a1a] text-white text-[13px] font-semibold shrink-0">{n}</span>
    {children}
  </h2>
);

const CURRENCY_SYMBOLS = { eur: '€', usd: '$', gbp: '£', pln: 'zł', sek: 'kr' };
const stripeCache = {};
const getStripe = (pk) => {
  if (!pk) return null;
  // If a previous load failed, drop the rejected promise so we can retry cleanly
  // (avoids the "refresh 5-6 times" symptom when Stripe.js load is flaky).
  if (!stripeCache[pk]) {
    stripeCache[pk] = loadStripe(pk).catch((e) => { delete stripeCache[pk]; throw e; });
  }
  return stripeCache[pk];
};

// Mobile collapsible order summary (Shopify-style bar at the very top).
const MobileOrderBar = ({ cfg, open, onToggle, accent, L }) => (
  <div className="lg:hidden bg-[#fafafa] border-b border-zinc-200" data-testid="paylink-mobile-summary">
    <button type="button" onClick={onToggle} data-testid="paylink-mobile-summary-toggle"
      className="w-full max-w-[1000px] mx-auto px-4 py-3.5 flex items-center justify-between">
      <span className="flex items-center gap-2 text-sm font-medium" style={{ color: accent || '#1a1a1a' }}>
        <ShoppingBag className="h-4 w-4" strokeWidth={1.8} />
        {open ? L.hide_summary : L.show_summary}
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </span>
      <span className="text-[17px] font-bold text-[#1a1a1a]">{money(cfg.price, cfg.currency)}</span>
    </button>
    {open && (
      <div className="max-w-[1000px] mx-auto px-4 pb-4 border-t border-zinc-200/70 pt-4">
        <div className="flex gap-3 items-center">
          <div className="relative shrink-0">
            {cfg.image_url ? <img src={resolveImageUrl(cfg.image_url)} alt={cfg.name} className="w-14 h-14 object-cover rounded-lg border border-zinc-200" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} /> : <div className="w-14 h-14 rounded-lg bg-zinc-100 border border-zinc-200" />}
            <span className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1 rounded-full bg-[#6B7280] text-white text-[11px] font-semibold flex items-center justify-center">1</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm text-[#1a1a1a] leading-tight">{cfg.name}</div>
            {cfg.description ? <div className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{cfg.description}</div> : null}
          </div>
          <div className="text-sm font-medium text-[#1a1a1a] shrink-0">{money(cfg.price, cfg.currency)}</div>
        </div>
        <div className="mt-4 pt-3 border-t border-zinc-200 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-[#555]">{L.subtotal}</span><span className="font-medium">{money(cfg.price, cfg.currency)}</span></div>
          <div className="flex justify-between items-baseline pt-1"><span className="text-base font-semibold">{L.total}</span><span className="text-[20px] font-bold"><span className="text-xs font-medium text-zinc-400 mr-1 uppercase">{cfg.currency}</span>{money(cfg.price, cfg.currency)}</span></div>
        </div>
      </div>
    )}
  </div>
);

const buildAppearance = (accent) => ({
  theme: 'stripe',
  variables: {
    colorPrimary: accent || '#111111',
    borderRadius: '10px',
    fontFamily: 'ui-sans-serif, system-ui, sans-serif',
  },
});

const money = (v, cur) => `${Number(v || 0).toFixed(2)} ${CURRENCY_SYMBOLS[cur] || (cur || '').toUpperCase()}`;

// Persist the in-flight PaymentIntent per link so retries AND full page reloads
// reuse the same PI (no duplicate transactions). Cleared on success.
const rememberPI = (id, ref, data) => {
  ref.current = { tx_id: data.tx_id, payment_intent_id: data.payment_intent_id, client_secret: data.client_secret };
  try { sessionStorage.setItem(`pl_pi_${id}`, JSON.stringify(ref.current)); } catch { /* storage off */ }
};
const clearPI = (id) => { try { sessionStorage.removeItem(`pl_pi_${id}`); } catch { /* storage off */ } };

// Default address country derived from the checkout language.
const LANG_COUNTRY = { fr: 'FR', nl: 'NL', es: 'ES', it: 'IT', de: 'DE', en: 'GB', sv: 'SE' };

const emptyForm = {
  email: '', first_name: '', last_name: '', address1: '',
  city: '', postal_code: '', country: 'FR', phone: '',
};

// Apple Pay / Google Pay express button for a payment link. Rendered at the very
// top of the checkout. Auto-hides (fully unmounts) when no wallet is available.
const PayLinkExpress = ({ cfg, linkId, payIntentRef, L }) => {
  const stripe = useStripe();
  const elements = useElements();
  const nav = useNavigate();
  const [available, setAvailable] = useState(false);
  const [dead, setDead] = useState(false);

  const onConfirm = async (event) => {
    if (!stripe || !elements) return;
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) { toast.error(submitError.message || L.err_payment); return; }
      const sa = event.shippingAddress || {};
      const addr = sa.address || {};
      const bd = event.billingDetails || {};
      const fullName = (sa.name || bd.name || '').trim();
      const parts = fullName.split(/\s+/);
      const customer = {
        email: bd.email || '',
        first_name: parts[0] || 'Client',
        last_name: parts.slice(1).join(' ') || '-',
        address1: addr.line1 || '',
        city: addr.city || '',
        postal_code: addr.postal_code || '',
        country: (addr.country || 'FR').toUpperCase(),
        phone: bd.phone || '',
      };
      const { data } = await publicApi.post(`${API}/public/pay/${linkId}/create-payment-intent`, { customer, reuse_tx_id: payIntentRef?.current?.tx_id || '' });
      if (payIntentRef) rememberPI(linkId, payIntentRef, data);
      if (data.already_paid) {
        clearPI(linkId);
        nav(`/checkout/success?pi=${data.payment_intent_id}`);
        return;
      }
      const piId = data.payment_intent_id;
      const billing_details = {
        name: fullName || '', email: bd.email || '', phone: bd.phone || '',
        address: { line1: addr.line1 || '', city: addr.city || '', state: addr.state || '', postal_code: addr.postal_code || '', country: (addr.country || 'FR').toUpperCase() },
      };
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements, clientSecret: data.client_secret,
        confirmParams: { return_url: `${window.location.origin}/checkout/success?pi=${piId}`, payment_method_data: { billing_details } },
        redirect: 'if_required',
      });
      if (error) { toast.error(error.message || L.err_payment); return; }
      if (paymentIntent && paymentIntent.status === 'requires_action') {
        const { error: naError } = await stripe.handleNextAction({ clientSecret: data.client_secret });
        if (naError) { toast.error(naError.message || L.err_generic); return; }
      }
      // Purchase pixel is fired on the success page (covers redirect methods too).
      clearPI(linkId);
      nav(`/checkout/success?pi=${(paymentIntent && paymentIntent.id) || piId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || L.payment_failed);
    }
  };

  if (dead) return null;
  return (
    <div className={available ? 'mb-6' : 'hidden'} data-testid="paylink-express-zone">
      <ExpressCheckoutElement
        options={{ buttonHeight: 50, paymentMethods: { applePay: 'auto', googlePay: 'auto', amazonPay: 'never', paypal: 'never', link: 'never' } }}
        onReady={(e) => { const has = !!(e && e.availablePaymentMethods); setAvailable(has); if (!has) setDead(true); }}
        onClick={({ resolve }) => resolve({
          emailRequired: true, phoneNumberRequired: true, shippingAddressRequired: true,
          shippingRates: [{ id: 'std', displayName: 'Standard', amount: 0 }],
          lineItems: [{ name: cfg.name, amount: Math.max(50, Math.round(Number(cfg.price) * 100)) }],
        })}
        onConfirm={onConfirm}
        onShippingAddressChange={({ resolve }) => resolve()}
        onShippingRateChange={({ resolve }) => resolve()}
      />
      <div className="flex items-center gap-3 mt-5">
        <span className="flex-1 h-px bg-zinc-200" />
        <span className="text-xs text-zinc-400">{L.express_or}</span>
        <span className="flex-1 h-px bg-zinc-200" />
      </div>
    </div>
  );
};

function PayForm({ cfg, form, accent, shopify, onFail, payIntentRef, L }) {
  const stripe = useStripe();
  const elements = useElements();
  const nav = useNavigate();
  const [ready, setReady] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [elemComplete, setElemComplete] = useState(false);  // card fields fully filled?
  const [errMsg, setErrMsg] = useState('');
  const [sugg, setSugg] = useState([]);
  const [sLoading, setSLoading] = useState(false);
  const [sFocus, setSFocus] = useState(false);
  const debRef = useRef(null);

  // Reliability: if the Payment Element hasn't mounted within 8s, ask the parent
  // to remount the whole Stripe <Elements> tree once (fresh key). Fixes the
  // "refresh 5-6 times before the card form appears" problem without the buyer
  // having to reload the page.
  useEffect(() => {
    if (ready) return undefined;
    const to = setTimeout(() => { if (!ready && onFail) onFail('timeout'); }, 12000);
    return () => clearTimeout(to);
  }, [ready, onFail]);

  const onAddr = (v) => {
    form.set('address1', v);
    if (debRef.current) clearTimeout(debRef.current);
    if (!v || v.length < 3) { setSugg([]); return; }
    setSLoading(true);
    debRef.current = setTimeout(async () => {
      try {
        const r = await publicApi.get(`${API}/public/address-search?q=${encodeURIComponent(v)}&limit=5&country=${encodeURIComponent(form.country || 'FR')}`);
        setSugg(r.data.results || []);
      } catch { setSugg([]); } finally { setSLoading(false); }
    }, 250);
  };
  const pickAddr = (s) => {
    form.set('address1', s.address1 || form.address1);
    form.set('city', s.city || form.city);
    form.set('postal_code', s.postal_code || form.postal_code);
    form.set('country', s.country || form.country);
    setSugg([]); setSFocus(false);
  };

  const complete = form.email && form.first_name && form.last_name && form.address1 && form.city && form.postal_code && form.country;

  const pay = async (e) => {
    e.preventDefault();
    if (processing) return;
    if (!stripe || !elements || !ready) { toast.error(L.err_loading); return; }
    if (!complete) { setErrMsg(L.err_incomplete_contact); toast.error(L.toast_incomplete_contact); return; }
    if (!elemComplete) { setErrMsg(L.err_card_missing); toast.error(L.toast_card_missing); return; }
    setProcessing(true);
    setErrMsg('');
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) { setErrMsg(submitError.message || L.err_check_payment); setProcessing(false); return; }
      const { data } = await publicApi.post(`${API}/public/pay/${cfg.id}/create-payment-intent`, {
        customer: {
          email: form.email, first_name: form.first_name, last_name: form.last_name,
          address1: form.address1, city: form.city, postal_code: form.postal_code,
          country: (form.country || 'FR').toUpperCase(), phone: form.phone || '',
        },
        reuse_tx_id: payIntentRef?.current?.tx_id || '',
      });
      // Remember this PI so a retry (or page reload) reuses it, never duplicates.
      if (payIntentRef) rememberPI(cfg.id, payIntentRef, data);
      // Already paid on a previous attempt → skip re-charging, go to success.
      if (data.already_paid) {
        clearPI(cfg.id);
        nav(`/checkout/success?pi=${data.payment_intent_id}`);
        return;
      }
      const clientSecret = data.client_secret;
      const piId = data.payment_intent_id;
      const billing_details = {
        name: `${(form.first_name || '').trim()} ${(form.last_name || '').trim()}`.trim(),
        email: form.email || '', phone: form.phone || '',
        address: {
          line1: form.address1 || '', city: form.city || '',
          postal_code: form.postal_code || '', country: (form.country || 'FR').toUpperCase(),
        },
      };
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements, clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/checkout/success?pi=${piId}`,
          payment_method_data: { billing_details },
        },
        redirect: 'if_required',
      });
      if (error) { setErrMsg(error.message || L.err_payment); toast.error(error.message || L.err_payment); setProcessing(false); return; }
      if (paymentIntent && paymentIntent.status === 'requires_action') {
        const { error: naError } = await stripe.handleNextAction({ clientSecret });
        if (naError) { setErrMsg(naError.message || L.err_payment); toast.error(naError.message || L.err_generic); setProcessing(false); return; }
      }
      // Purchase pixel is fired on the success page (covers redirect methods too).
      clearPI(cfg.id);
      nav(`/checkout/success?pi=${(paymentIntent && paymentIntent.id) || piId}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || L.generic_error;
      setErrMsg(typeof msg === 'string' ? msg : L.err_generic); toast.error(L.payment_failed); setProcessing(false);
    }
  };

  return (
    <form onSubmit={pay} className="space-y-5" data-testid="paylink-form">
      {shopify && <StepTitle n={1}>{L.step_contact}</StepTitle>}
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label className="text-xs text-zinc-500">{L.email}</Label>
          <Input data-testid="paylink-email" type="email" value={form.email} onChange={(e) => form.set('email', e.target.value)} placeholder={L.email_ph} className="mt-1 h-11" />
        </div>
        {shopify && <div className="col-span-2 -mb-1 mt-1"><StepTitle n={2}>{L.step_shipping}</StepTitle></div>}
        <div>
          <Label className="text-xs text-zinc-500">{L.first_name}</Label>
          <Input data-testid="paylink-first-name" value={form.first_name} onChange={(e) => form.set('first_name', e.target.value)} className="mt-1 h-11" />
        </div>
        <div>
          <Label className="text-xs text-zinc-500">{L.last_name}</Label>
          <Input data-testid="paylink-last-name" value={form.last_name} onChange={(e) => form.set('last_name', e.target.value)} className="mt-1 h-11" />
        </div>
        <div className="col-span-2 relative">
          <Label className="text-xs text-zinc-500">{L.address}</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
            <Input
              data-testid="paylink-address" value={form.address1}
              onChange={(e) => onAddr(e.target.value)}
              onFocus={() => setSFocus(true)}
              onBlur={() => setTimeout(() => setSFocus(false), 200)}
              placeholder={L.address_ph}
              className="mt-1 h-11 pl-9" autoComplete="off"
            />
          </div>
          {sFocus && (sLoading || sugg.length > 0) && (
            <div data-testid="paylink-address-suggestions" className="absolute z-30 mt-1 w-full bg-white border border-zinc-200 rounded-lg shadow-lg max-h-64 overflow-auto">
              {sLoading && <div className="px-3 py-2 text-xs text-zinc-400 flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> {L.searching}</div>}
              {sugg.map((s, i) => (
                <button key={`${s.label}-${i}`} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pickAddr(s)}
                  data-testid={`paylink-address-option-${i}`} className="w-full text-left px-3 py-2 hover:bg-zinc-50 border-b border-zinc-100 last:border-b-0">
                  <div className="text-sm font-medium text-[#1a1a1a]">{s.address1}</div>
                  <div className="text-xs text-zinc-500">{s.postal_code} {s.city}{s.context ? ` · ${s.context}` : ''}</div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <Label className="text-xs text-zinc-500">{L.city}</Label>
          <Input data-testid="paylink-city" value={form.city} onChange={(e) => form.set('city', e.target.value)} className="mt-1 h-11" />
        </div>
        <div>
          <Label className="text-xs text-zinc-500">{L.postal_code}</Label>
          <Input data-testid="paylink-postal" value={form.postal_code} onChange={(e) => form.set('postal_code', e.target.value)} className="mt-1 h-11" />
        </div>
        <div>
          <Label className="text-xs text-zinc-500">{L.country}</Label>
          <select
            data-testid="paylink-country" value={form.country}
            onChange={(e) => { form.set('country', e.target.value.toUpperCase()); setSugg([]); }}
            className="mt-1 h-11 w-full rounded-md border border-zinc-300 bg-white px-3 text-sm"
          >
            {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs text-zinc-500">{L.phone_opt}</Label>
          <Input data-testid="paylink-phone" value={form.phone} onChange={(e) => form.set('phone', e.target.value)} className="mt-1 h-11" />
        </div>
      </div>

      {shopify && <StepTitle n={3}>{L.step_payment}</StepTitle>}
      {shopify && <p className="text-xs text-zinc-500 -mt-3 flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> {L.secure_all}</p>}
      <div className={shopify ? 'rounded-lg border border-zinc-300 p-4' : 'pt-1'}>
        <PaymentElement
          onReady={() => setReady(true)}
          onChange={(ev) => setElemComplete(!!ev.complete)}
          onLoadError={() => { if (onFail) onFail('loaderror'); }}
          options={{ layout: shopify ? { type: 'accordion', defaultCollapsed: false, radios: 'if_multiple', spacedAccordionItems: true } : 'tabs', wallets: { link: 'never', applePay: 'never', googlePay: 'never' }, fields: { billingDetails: 'never' } }}
        />
        {!ready && (
          <div className="flex items-center justify-center gap-2 py-6 text-zinc-400 text-sm" data-testid="paylink-element-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> {L.loading_payment}
          </div>
        )}
      </div>

      {errMsg && (
        <div data-testid="paylink-error" className="text-xs text-red-700 bg-red-50 border border-red-200 p-3 rounded-md">{errMsg}</div>
      )}

      {/* Desktop inline pay button */}
      <Button
        type="submit"
        disabled={!stripe || processing || !ready || !elemComplete}
        data-testid="paylink-pay-button"
        style={{ backgroundColor: accent || '#0A0A0A' }}
        className={`hidden lg:flex w-full text-white font-semibold hover:opacity-90 transition disabled:opacity-60 items-center justify-center ${shopify ? 'h-14 rounded-lg text-[17px]' : 'h-12 rounded-md text-base'}`}
      >
        {processing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {L.processing}</> : <><Lock className="h-4 w-4 mr-2" /> {L.pay} {money(cfg.price, cfg.currency)}</>}
      </Button>
      {ready && !elemComplete && (
        <p className="hidden lg:block text-[11px] text-zinc-400 text-center -mt-2" data-testid="paylink-card-hint">{L.card_gate_hint}</p>
      )}
      <p className="hidden lg:flex text-xs text-zinc-400 text-center items-center justify-center gap-1.5">
        <ShieldCheck className="h-3.5 w-3.5" /> {L.secure_stripe}
      </p>

      {/* Mobile sticky pay bar (moves with the checkout like store checkouts) */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-t border-zinc-200 px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] shadow-[0_-8px_30px_rgba(0,0,0,0.08)]">
        <Button
          type="submit"
          disabled={!stripe || processing || !ready || !elemComplete}
          data-testid="paylink-pay-button-mobile"
          style={{ backgroundColor: accent || '#0A0A0A' }}
          className="w-full h-14 rounded-lg text-white text-[17px] font-semibold hover:opacity-90 transition active:scale-[0.98] disabled:opacity-60 flex items-center justify-center"
        >
          {processing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {L.processing}</> : <><Lock className="h-4 w-4 mr-2" /> {L.pay} {money(cfg.price, cfg.currency)}</>}
        </Button>
        <p className="text-[11px] text-zinc-400 text-center mt-1.5 flex items-center justify-center gap-1"><ShieldCheck className="h-3 w-3" /> {ready && !elemComplete ? L.card_gate_hint_short : L.secure_stripe}</p>
      </div>
    </form>
  );
}

export default function PayLink() {
  const { linkId } = useParams();
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setFormState] = useState(emptyForm);
  const [hostedBusy, setHostedBusy] = useState(false);
  const [elemKey, setElemKey] = useState(0);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const retriesRef = useRef(0);
  const mounted = useRef(true);
  // Shared across the card form AND the wallet button: reuse the same PI on every
  // retry so declined/abandoned attempts never create duplicate transactions.
  const payIntentRef = useRef(null);
  // Restore an in-flight PI after a full page reload so refreshing does not
  // spawn a fresh transaction either.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(`pl_pi_${linkId}`);
      if (raw) payIntentRef.current = JSON.parse(raw);
    } catch { /* storage off */ }
  }, [linkId]);

  const handleElemFail = () => {
    if (retriesRef.current < 3) {
      retriesRef.current += 1;
      setElemKey((k) => k + 1);
    }
  };

  useEffect(() => {
    mounted.current = true;
    (async () => {
      try {
        const { data } = await publicApi.get(`${API}/public/pay/${linkId}`);
        if (mounted.current) setCfg(data);
      } catch (e) {
        if (mounted.current) setError(e?.response?.data?.detail || '__unavailable__');
      } finally {
        if (mounted.current) setLoading(false);
      }
    })();
    return () => { mounted.current = false; };
  }, [linkId]);

  // Inject Meta/TikTok pixels + fire InitiateCheckout once the link config loads.
  useEffect(() => {
    if (!cfg) return;
    if (cfg.meta_pixel_id || cfg.tiktok_pixel_id) {
      injectLinkPixels(cfg.meta_pixel_id, cfg.tiktok_pixel_id);
      fireLinkPixel('checkout', {
        metaPixelId: cfg.meta_pixel_id, tiktokPixelId: cfg.tiktok_pixel_id,
        amount: cfg.price, currency: (cfg.currency || 'eur').toUpperCase(),
      });
    }
  }, [cfg]);

  // Pre-select the address country from the checkout LANGUAGE (nl → NL, es → ES,
  // it → IT, de → DE, fr → FR, en → GB). Applied once when the link config loads;
  // the buyer can still change it manually afterwards.
  const countryApplied = useRef(false);
  useEffect(() => {
    if (!cfg || countryApplied.current) return;
    const lang = resolvePayLinkLang(cfg.language);
    const cc = LANG_COUNTRY[lang];
    if (cc && COUNTRIES.some((c) => c.code === cc)) {
      countryApplied.current = true;
      setFormState((p) => ({ ...p, country: cc }));
    }
  }, [cfg]);

  const set = (k, v) => setFormState((p) => ({ ...p, [k]: v }));
  const formApi = { ...form, set };

  const accent = cfg?.branding?.accent_color || '';
  const L = payLinkT(resolvePayLinkLang(cfg?.language));
  const stripeInstance = useMemo(() => getStripe(cfg?.stripe_public_key), [cfg?.stripe_public_key]);
  const elementsOptions = useMemo(() => (cfg ? {
    mode: 'payment',
    amount: Math.max(50, Math.round(Number(cfg.price) * 100)),
    currency: cfg.currency,
    excludedPaymentMethodTypes: ['amazon_pay', 'paypal'],
    ...(cfg.save_card ? { paymentMethodOptions: { card: { setup_future_usage: 'off_session' } } } : {}),
    appearance: buildAppearance(accent),
  } : null), [cfg, accent]);

  const payHosted = async () => {
    if (hostedBusy) return;
    if (!(form.email && form.first_name && form.last_name && form.address1 && form.city && form.postal_code && form.country)) {
      toast.error(L.complete_info);
      return;
    }
    setHostedBusy(true);
    try {
      const { data } = await publicApi.post(`${API}/public/pay/${linkId}/create-session`, {
        customer: {
          email: form.email, first_name: form.first_name, last_name: form.last_name,
          address1: form.address1, city: form.city, postal_code: form.postal_code,
          country: (form.country || 'FR').toUpperCase(), phone: form.phone || '',
        },
      });
      if (data.session_url) window.location.href = data.session_url;
      else { toast.error(L.session_unavailable); setHostedBusy(false); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || L.payment_failed);
      setHostedBusy(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-zinc-50"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>;
  }
  if (error || !cfg) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-6">
        <div className="text-center max-w-sm" data-testid="paylink-unavailable">
          <p className="text-lg font-semibold text-zinc-900">{L.unavailable_title}</p>
          <p className="text-sm text-zinc-500 mt-2">{error && error !== '__unavailable__' ? error : L.unavailable_desc}</p>
        </div>
      </div>
    );
  }

  const brandName = cfg.branding?.display_name || L.brand_default;

  // SHOPIFY-style layout (element mode only): 2 columns, trust-optimized.
  if (cfg.template === 'shopify' && cfg.checkout_mode === 'element') {
    return (
      <div className="min-h-screen bg-white" style={{ fontFamily: SHOP_FONT, color: '#1a1a1a' }} data-testid="paylink-page">
        <header className="border-b border-zinc-200">
          <div className="max-w-[1000px] mx-auto px-4 sm:px-6 h-[68px] flex items-center justify-between gap-3">
            {cfg.branding?.logo_url
              ? <img src={cfg.branding.logo_url} alt={brandName} className="h-9 max-w-[180px] object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              : <span className="text-[22px] font-bold tracking-tight text-[#1a1a1a] truncate">{brandName}</span>}
            <div className="flex items-center gap-4 shrink-0">
              <div className="hidden sm:flex items-center gap-1.5">
                <span className="h-5 px-1.5 rounded bg-white border border-zinc-200 text-[9px] font-bold flex items-center" style={{ color: '#1A1F71' }}>VISA</span>
                <span className="h-5 w-8 rounded bg-white border border-zinc-200 flex items-center justify-center"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: '#EB001B' }} /><span className="w-3 h-3 rounded-full -ml-1.5" style={{ backgroundColor: '#F79E1B' }} /></span>
                <span className="h-5 px-1.5 rounded text-white text-[9px] font-bold flex items-center" style={{ backgroundColor: '#2E77BC' }}>AMEX</span>
              </div>
              <ShoppingBag className="h-6 w-6 text-[#1a1a1a]" strokeWidth={1.6} />
            </div>
          </div>
        </header>
        <MobileOrderBar cfg={cfg} open={summaryOpen} onToggle={() => setSummaryOpen((v) => !v)} accent={accent} L={L} />
        <div className="max-w-[1000px] mx-auto lg:grid lg:grid-cols-[1fr_minmax(0,400px)]">
          {/* LEFT: form (extra bottom padding on mobile for the sticky pay bar) */}
          <div className="px-4 sm:px-6 lg:pr-12 py-6 lg:py-10 pb-32 lg:pb-10">
            {stripeInstance && elementsOptions && (
              <Elements key={`xp-${elemKey}`} stripe={stripeInstance} options={elementsOptions}>
                <PayLinkExpress cfg={cfg} linkId={linkId} payIntentRef={payIntentRef} L={L} />
              </Elements>
            )}
            {stripeInstance && elementsOptions ? (
              <Elements key={elemKey} stripe={stripeInstance} options={elementsOptions}>
                <PayForm cfg={cfg} form={formApi} accent={accent} shopify onFail={handleElemFail} payIntentRef={payIntentRef} L={L} />
              </Elements>
            ) : (
              <div className="h-40 flex items-center justify-center text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
            )}
            <div className="mt-8 pt-6 border-t border-zinc-200 flex flex-col sm:flex-row items-center justify-between gap-3" data-testid="paylink-trust-footer">
              <span className="text-xs text-zinc-500 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> {L.secure_short}</span>
              <div className="flex items-center gap-1.5">
                <span className="h-5 px-1.5 rounded bg-white border border-zinc-200 text-[9px] font-bold flex items-center" style={{ color: '#1A1F71' }}>VISA</span>
                <span className="h-5 w-8 rounded bg-white border border-zinc-200 flex items-center justify-center"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: '#EB001B' }} /><span className="w-3 h-3 rounded-full -ml-1.5" style={{ backgroundColor: '#F79E1B' }} /></span>
                <span className="h-5 px-1.5 rounded text-white text-[9px] font-bold flex items-center" style={{ backgroundColor: '#2E77BC' }}>AMEX</span>
              </div>
            </div>
          </div>
          {/* RIGHT: gray order summary — desktop only (mobile uses the top bar) */}
          <div className="hidden lg:block bg-[#fafafa] border-l border-zinc-200">
            <div className="px-8 py-10 lg:sticky lg:top-0" data-testid="paylink-product-card">
              <div className="flex gap-3 items-center">
                <div className="relative shrink-0">
                  {cfg.image_url ? <img src={resolveImageUrl(cfg.image_url)} alt={cfg.name} className="w-16 h-16 object-cover rounded-lg border border-zinc-200" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} /> : <div className="w-16 h-16 rounded-lg bg-zinc-100 border border-zinc-200" />}
                  <span className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1 rounded-full bg-[#6B7280] text-white text-[11px] font-semibold flex items-center justify-center">1</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-[#333] leading-tight" data-testid="paylink-product-name">{cfg.name}</div>
                  {cfg.description ? <div className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{cfg.description}</div> : null}
                </div>
                <div className="text-sm font-medium text-[#333] shrink-0">{money(cfg.price, cfg.currency)}</div>
              </div>
              <div className="mt-6 pt-4 border-t border-zinc-200 space-y-2.5 text-sm">
                <div className="flex justify-between"><span className="text-[#555]">{L.subtotal}</span><span className="font-medium text-[#333]">{money(cfg.price, cfg.currency)}</span></div>
                <div className="border-t border-zinc-200 pt-3 flex justify-between items-baseline">
                  <span className="text-base font-semibold text-[#1a1a1a]">{L.total}</span>
                  <span className="text-[24px] font-bold text-[#1a1a1a]" data-testid="paylink-price"><span className="text-xs font-medium text-zinc-400 mr-1.5 uppercase">{cfg.currency}</span>{money(cfg.price, cfg.currency)}</span>
                </div>
                <p className="text-xs text-zinc-400">{L.taxes_incl}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // LUMEN layout (element mode): premium, centered single column, warm palette.
  if (cfg.template === 'lumen' && cfg.checkout_mode === 'element') {
    return (
      <div className="min-h-screen bg-[#FAF7F2]" style={{ fontFamily: SHOP_FONT, color: '#1a1a1a' }} data-testid="paylink-page">
        <div className="max-w-xl mx-auto px-4 sm:px-6 py-10 lg:py-16 pb-32 lg:pb-16">
          <div className="flex justify-center mb-8">
            {cfg.branding?.logo_url
              ? <img src={cfg.branding.logo_url} alt={brandName} className="h-10 max-w-[200px] object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              : <span className="text-2xl font-bold tracking-tight">{brandName}</span>}
          </div>
          <div className="bg-white rounded-3xl border border-[#ECE6DD] shadow-[0_10px_40px_rgba(0,0,0,0.04)] overflow-hidden mb-6" data-testid="paylink-product-card">
            {cfg.image_url ? (
              <div className="aspect-[16/9] bg-[#F3EEE7]">
                <img src={resolveImageUrl(cfg.image_url)} alt={cfg.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }} />
              </div>
            ) : null}
            <div className="p-6 sm:p-8">
              <p className="text-[11px] uppercase tracking-[0.2em] text-zinc-400 mb-1.5">{L.summary_label}</p>
              <h1 className="text-xl font-semibold leading-tight" data-testid="paylink-product-name">{cfg.name}</h1>
              {cfg.description ? <p className="text-sm text-zinc-500 mt-2 whitespace-pre-line">{cfg.description}</p> : null}
              <div className="mt-5 pt-5 border-t border-[#ECE6DD] flex items-center justify-between">
                <span className="text-sm text-zinc-500">{L.total_due}</span>
                <span className="text-[26px] font-bold" data-testid="paylink-price">{money(cfg.price, cfg.currency)}</span>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-3xl border border-[#ECE6DD] shadow-[0_10px_40px_rgba(0,0,0,0.04)] p-6 sm:p-8">
            {stripeInstance && elementsOptions && (
              <Elements key={`xp-${elemKey}`} stripe={stripeInstance} options={elementsOptions}>
                <PayLinkExpress cfg={cfg} linkId={linkId} payIntentRef={payIntentRef} L={L} />
              </Elements>
            )}
            {stripeInstance && elementsOptions ? (
              <Elements key={elemKey} stripe={stripeInstance} options={elementsOptions}>
                <PayForm cfg={cfg} form={formApi} accent={accent} onFail={handleElemFail} payIntentRef={payIntentRef} L={L} />
              </Elements>
            ) : (
              <div className="h-40 flex items-center justify-center text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="paylink-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 lg:py-14">
        <div className="flex items-center gap-2.5 mb-8">
          {cfg.branding?.logo_url
            ? <img src={cfg.branding.logo_url} alt={brandName} className="h-8 w-auto" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            : <div className="h-8 px-3 rounded-md flex items-center text-white text-sm font-semibold" style={{ backgroundColor: accent || '#0A0A0A' }}>{brandName}</div>}
        </div>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-10 items-start">
          {/* Product summary */}
          <div className="lg:sticky lg:top-14 order-1">
            <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden" data-testid="paylink-product-card">
              {cfg.image_url ? (
                <div className="aspect-[4/3] bg-zinc-100">
                  <img src={resolveImageUrl(cfg.image_url)} alt={cfg.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }} />
                </div>
              ) : null}
              <div className="p-6">
                <p className="text-xs uppercase tracking-wide text-zinc-400 mb-1">{L.summary_label}</p>
                <h1 className="text-xl font-semibold text-zinc-900" data-testid="paylink-product-name">{cfg.name}</h1>
                {cfg.description ? <p className="text-sm text-zinc-500 mt-2 whitespace-pre-line">{cfg.description}</p> : null}
                <div className="mt-5 pt-5 border-t border-zinc-100 flex items-center justify-between">
                  <span className="text-sm text-zinc-500">{L.total_due}</span>
                  <span className="text-2xl font-bold text-zinc-900" data-testid="paylink-price">{money(cfg.price, cfg.currency)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment */}
          <div className="order-2">
            <div className="bg-white rounded-2xl border border-zinc-200 p-6">
              <h2 className="text-base font-semibold text-zinc-900 mb-4">{L.your_info_payment}</h2>
              {cfg.checkout_mode === 'hosted' ? (
                <div className="space-y-5" data-testid="paylink-hosted">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2"><Label className="text-xs text-zinc-500">{L.email}</Label><Input data-testid="paylink-email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className="mt-1 h-11" /></div>
                    <div><Label className="text-xs text-zinc-500">{L.first_name}</Label><Input data-testid="paylink-first-name" value={form.first_name} onChange={(e) => set('first_name', e.target.value)} className="mt-1 h-11" /></div>
                    <div><Label className="text-xs text-zinc-500">{L.last_name}</Label><Input data-testid="paylink-last-name" value={form.last_name} onChange={(e) => set('last_name', e.target.value)} className="mt-1 h-11" /></div>
                    <div className="col-span-2"><Label className="text-xs text-zinc-500">{L.address}</Label><Input data-testid="paylink-address" value={form.address1} onChange={(e) => set('address1', e.target.value)} className="mt-1 h-11" /></div>
                    <div><Label className="text-xs text-zinc-500">{L.city}</Label><Input data-testid="paylink-city" value={form.city} onChange={(e) => set('city', e.target.value)} className="mt-1 h-11" /></div>
                    <div><Label className="text-xs text-zinc-500">{L.postal_code}</Label><Input data-testid="paylink-postal" value={form.postal_code} onChange={(e) => set('postal_code', e.target.value)} className="mt-1 h-11" /></div>
                    <div><Label className="text-xs text-zinc-500">{L.country}</Label><Input data-testid="paylink-country" value={form.country} onChange={(e) => set('country', e.target.value.toUpperCase())} maxLength={2} className="mt-1 h-11 uppercase" /></div>
                    <div><Label className="text-xs text-zinc-500">{L.phone}</Label><Input data-testid="paylink-phone" value={form.phone} onChange={(e) => set('phone', e.target.value)} className="mt-1 h-11" /></div>
                  </div>
                  <Button data-testid="paylink-hosted-pay-button" onClick={payHosted} disabled={hostedBusy} style={{ backgroundColor: accent || '#0A0A0A' }} className="w-full h-12 rounded-md text-white text-base font-semibold hover:opacity-90 transition disabled:opacity-60 flex items-center justify-center">
                    {hostedBusy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {L.redirecting}</> : <><Lock className="h-4 w-4 mr-2" /> {L.pay} {money(cfg.price, cfg.currency)}</>}
                  </Button>
                  <p className="text-xs text-zinc-400 text-center flex items-center justify-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> {L.hosted_redirect_note}</p>
                </div>
              ) : (
                stripeInstance && elementsOptions ? (
                  <Elements stripe={stripeInstance} options={elementsOptions}>
                    <PayForm cfg={cfg} form={formApi} accent={accent} payIntentRef={payIntentRef} L={L} />
                  </Elements>
                ) : (
                  <div className="h-40 flex items-center justify-center text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
